import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, Datepicker, Input, Switch } from '@ibsys/kit';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import type {
  AttributeFilter,
  AttributeFilterBoolean,
  AttributeFilterDateRange,
  AttributeFilterNumeric,
  AttributeFilterSelect,
  AttributeFilterText,
  AttributeFilterTimestampRange,
} from '@/entities/dashboards';
import { getAttributeFilterKind } from '../model/attributeFilters';

type Props = {
  attribute: ConversationAttributeItem;
  filter: AttributeFilter;
  onChange: (next: AttributeFilter) => void;
};

/**
 * Поле ввода значения атрибутного фильтра. Выбор компонента — по `dataType` атрибута.
 * Лейбл компонента не задаётся: имя атрибута выводится во внешней шапке ячейки.
 */
export const AttributeFilterField: FC<Props> = ({ attribute, filter, onChange }) => {
  const { t } = useTranslation('common');
  const kind = getAttributeFilterKind(attribute);
  const path = filter.path;

  // Опции для SELECT/MULTISELECT — из selectValues атрибута.
  // Лейбл — `value` (не `description`). В фильтр (`f.values`) уходит `code`:
  // бэк сравнивает с колонками `value_select_code` / `value_multiselect_code`
  // (хранят коды, не пользовательские лейблы).
  const selectOptions = useMemo(
    () =>
      (attribute.selectValues ?? []).map((sv) => ({
        value: sv.code,
        label: sv.value,
      })),
    [attribute.selectValues],
  );

  if (kind === 'multiselect') {
    const f = filter as AttributeFilterSelect;
    return (
      <Combobox
        isMulti
        value={f.values}
        onChange={(v) => {
          const values = Array.isArray(v) ? v.map(String) : [];
          onChange({ path, values });
        }}
        options={selectOptions}
        placeholder={t('attributeFilters.valuesPlaceholder')}
        isSearchable
        className="w-full"
      />
    );
  }

  if (kind === 'select') {
    const f = filter as AttributeFilterSelect;
    const current = f.values[0] ?? '';
    return (
      <Combobox
        value={current}
        onChange={(v) => {
          const next = v == null || v === '' ? [] : [String(v)];
          onChange({ path, values: next });
        }}
        options={selectOptions}
        placeholder={t('attributeFilters.valuesPlaceholder')}
        isSearchable
        className="w-full"
      />
    );
  }

  if (kind === 'numeric') {
    const f = filter as AttributeFilterNumeric;
    return (
      <div className="flex gap-2">
        <Input
          type="number"
          placeholder={t('attributeFilters.min')}
          value={f.min ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            onChange({ path, min: raw === '' ? undefined : Number(raw), max: f.max });
          }}
          className="w-full"
        />
        <Input
          type="number"
          placeholder={t('attributeFilters.max')}
          value={f.max ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            onChange({ path, min: f.min, max: raw === '' ? undefined : Number(raw) });
          }}
          className="w-full"
        />
      </div>
    );
  }

  if (kind === 'text') {
    const f = filter as AttributeFilterText;
    return (
      <Input
        value={f.text}
        onChange={(e) => onChange({ path, text: e.target.value })}
        placeholder={t('attributeFilters.textPlaceholder')}
        className="w-full"
      />
    );
  }

  if (kind === 'boolean') {
    const f = filter as AttributeFilterBoolean;
    return <Switch value={f.bool} onChange={(v) => onChange({ path, bool: v })} label={f.bool ? t('yes') : t('no')} />;
  }

  if (kind === 'dateRange') {
    const f = filter as AttributeFilterDateRange;
    return (
      <Datepicker
        mode="range"
        outputType="iso-date"
        value={{ from: f.dateFrom, to: f.dateTo }}
        onChange={(v) => {
          const range = typeof v === 'object' && v !== null ? v : { from: undefined, to: undefined };
          onChange({ path, dateFrom: range.from || undefined, dateTo: range.to || undefined });
        }}
      />
    );
  }

  if (kind === 'timestampRange') {
    const f = filter as AttributeFilterTimestampRange;
    return (
      <Datepicker
        mode="range"
        showTimeInputs
        value={{ from: f.dateFrom, to: f.dateTo }}
        onChange={(v) => {
          const range = typeof v === 'object' && v !== null ? v : { from: undefined, to: undefined };
          onChange({ path, dateFrom: range.from || undefined, dateTo: range.to || undefined });
        }}
      />
    );
  }

  return null;
};
